import { useState, useEffect, useCallback, useRef } from "react";
import { useMbiraSynth, getDefaultSettings, MbiraSettings } from "./useMbiraSynth";
import { TUNINGS, TuningType, MbiraKeyDef } from "./tunings";
import { SONGS } from "./songs";
import { useMidi, midiNoteToFreq } from "../../hooks/useMidi";

export function RSMbira() {
  const { playKey, updateSettings, init } = useMbiraSynth();
  const [settings, setSettings] = useState<MbiraSettings>(getDefaultSettings);
  const [activeKeys, setActiveKeys] = useState<Set<string>>(new Set());
  const [selectedTuning, setSelectedTuning] = useState<TuningType>("nyamaropa");

  const [isPlaying, setIsPlaying] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [showDemoMenu, setShowDemoMenu] = useState(false);
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  const keys = TUNINGS[selectedTuning].keys;

  useEffect(() => {
    setActiveKeys(new Set());
  }, [selectedTuning]);

  const handleSettingChange = useCallback(
    (key: keyof MbiraSettings, value: number) => {
      setSettings((prev) => {
        const next = { ...prev, [key]: value };
        updateSettings(next);
        return next;
      });
    },
    [updateSettings]
  );

  const [velocitySensitivity, setVelocitySensitivity] = useState(0.7);

  const triggerKey = useCallback(
    (id: string, freq: number, velocity = 127) => {
      init();
      const keyDef = keys.find((k) => k.id === id);
      if (!keyDef) return;

      // Pan based on manual: left = -0.4, right = 0.3, upper = 0.5
      const panMap = { left: -0.4, right: 0.3, upper: 0.5 };
      const pan = panMap[keyDef.manual];
      playKey(freq, pan, velocity);

      setActiveKeys((prev) => new Set(prev).add(id));
      setTimeout(() => {
        setActiveKeys((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, 200);
    },
    [playKey, init, keys]
  );

  // MIDI support
  const midi = useMidi({
    onNoteOn: (midiNote, velocity) => {
      const midiFreq = midiNoteToFreq(midiNote);
      let closestKey: MbiraKeyDef | null = null;
      let closestDiff = Infinity;

      for (const key of keys) {
        const diff = Math.abs(key.freq - midiFreq);
        if (diff < closestDiff) {
          closestDiff = diff;
          closestKey = key;
        }
      }

      if (closestKey && closestDiff < closestKey.freq) {
        const effectiveVelocity = Math.round(127 - (127 - velocity) * velocitySensitivity);
        triggerKey(closestKey.id, closestKey.freq, effectiveVelocity);
      }
    },
  });

  const stopSong = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    setIsPlaying(false);
    setActiveKeys(new Set());
  }, []);

  const playSong = useCallback(
    (songId: string) => {
      const song = SONGS.find((s) => s.id === songId);
      if (!song) return;

      stopSong();
      init();
      setSelectedTuning("nyamaropa");
      setIsPlaying(true);

      let maxTime = 0;
      song.notes.forEach((n) => {
        const tuning = TUNINGS["nyamaropa"].keys;
        const key = tuning.find((k) => k.id === n.note);
        if (key) {
          const timeout = setTimeout(
            () => triggerKey(key.id, key.freq, n.velocity ?? 100),
            n.time * 1000
          );
          timeoutsRef.current.push(timeout);
          maxTime = Math.max(maxTime, n.time);
        }
      });

      const endTimeout = setTimeout(() => {
        setIsPlaying(false);
      }, (maxTime + 2) * 1000);
      timeoutsRef.current.push(endTimeout);
    },
    [stopSong, init, triggerKey]
  );

  useEffect(() => {
    return () => stopSong();
  }, [stopSong]);

  // Keyboard handler
  useEffect(() => {
    const keyMap = new Map<string, MbiraKeyDef>();
    keys.forEach((k) => k.keys.forEach((key) => keyMap.set(key, k)));
    const pressed = new Set<string>();

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (keyMap.has(key)) {
        e.preventDefault();
        e.stopPropagation();
        if (!pressed.has(key)) {
          pressed.add(key);
          const keyDef = keyMap.get(key)!;
          triggerKey(keyDef.id, keyDef.freq);
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      pressed.delete(e.key.toLowerCase());
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [triggerKey, keys]);

  // Separate keys by manual
  const leftKeys = keys.filter((k) => k.manual === "left");
  const rightKeys = keys.filter((k) => k.manual === "right");
  const upperKeys = keys.filter((k) => k.manual === "upper");

  return (
    <div className="max-w-md mx-auto select-none">
      {/* Body - darker wood for mbira */}
      <div className="bg-gradient-to-b from-[#2d2822] to-[#1a1610] rounded-[2rem] p-5 pt-6 shadow-[0_8px_32px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.03)]">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div className="w-20 flex justify-start">
            <button
              onClick={() => setShowLabels(!showLabels)}
              className={`text-[8px] tracking-wider px-2 py-1 rounded transition-colors ${
                showLabels
                  ? "bg-[#b89a5a] text-[#1a150f] font-semibold"
                  : "bg-[#1a150f] text-[#5a4a38] hover:text-[#7a6a50]"
              }`}
            >
              ABC
            </button>
          </div>
          <div className="text-[10px] font-medium tracking-[0.3em] text-[#7a6a50] uppercase">
            RS-Mbira
          </div>
          <div className="w-20 flex justify-end items-center gap-2">
            {midi.connected && (
              <input
                type="range"
                min="0"
                max="100"
                value={velocitySensitivity * 100}
                onChange={(e) => setVelocitySensitivity(Number(e.target.value) / 100)}
                className="w-10 h-1 accent-[#b89a5a]"
                title="Velocity sensitivity"
              />
            )}
            <div
              className={`text-[8px] tracking-wider px-2 py-1 rounded ${
                midi.connected
                  ? "bg-[#b89a5a] text-[#1a150f] font-semibold"
                  : "bg-[#1a150f] text-[#5a4a38]"
              }`}
              title={midi.connected ? midi.deviceName || "MIDI Connected" : "MIDI not connected"}
            >
              MIDI
            </div>
          </div>
        </div>

        {/* Mbira Key Assembly */}
        <div className="relative">
          {/* Bridge bar */}
          <div className="mx-auto w-[90%] h-2 bg-gradient-to-b from-[#8a8a8a] to-[#5a5a5a] rounded-sm shadow-[0_2px_4px_rgba(0,0,0,0.5)]" />

          {/* Keys - horizontal bars */}
          <div className="flex justify-center gap-4 -mt-1">
            {/* Left Manual (Bass) */}
            <div className="flex flex-col items-end gap-1 pt-2">
              {leftKeys.map((key, i) => (
                <MbiraKey
                  key={key.id}
                  keyDef={key}
                  isActive={activeKeys.has(key.id)}
                  onClick={() => triggerKey(key.id, key.freq)}
                  width={50 + i * 5}
                  showLabel={showLabels}
                  manual="left"
                />
              ))}
            </div>

            {/* Upper Manual (High) */}
            <div className="flex flex-col items-start gap-1 pt-2">
              {upperKeys.map((key, i) => (
                <MbiraKey
                  key={key.id}
                  keyDef={key}
                  isActive={activeKeys.has(key.id)}
                  onClick={() => triggerKey(key.id, key.freq)}
                  width={35 + i * 4}
                  showLabel={showLabels}
                  manual="upper"
                />
              ))}
            </div>

            {/* Right Manual (Mid) */}
            <div className="flex flex-col items-start gap-1 pt-2">
              {rightKeys.map((key, i) => (
                <MbiraKey
                  key={key.id}
                  keyDef={key}
                  isActive={activeKeys.has(key.id)}
                  onClick={() => triggerKey(key.id, key.freq)}
                  width={45 + i * 4}
                  showLabel={showLabels}
                  manual="right"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Sound hole */}
        <div className="flex justify-center mt-5">
          <div className="w-20 h-20 rounded-full bg-[#1a150f] shadow-[inset_0_4px_12px_rgba(0,0,0,0.8)]" />
        </div>

        {/* Controls */}
        <div className="mt-5 space-y-4">
          {/* Knobs */}
          <div className="flex justify-center gap-8">
            <Knob label="VOL" value={settings.volume} onChange={(v) => handleSettingChange("volume", v)} />
            <Knob label="DEC" value={settings.decay} onChange={(v) => handleSettingChange("decay", v)} />
            <Knob label="TONE" value={settings.tone} onChange={(v) => handleSettingChange("tone", v)} />
            <Knob label="REV" value={settings.reverb} onChange={(v) => handleSettingChange("reverb", v)} />
            <Knob label="BUZ" value={settings.buzz} onChange={(v) => handleSettingChange("buzz", v)} />
          </div>

          {/* Tuning Select */}
          <div className="flex justify-center">
            <select
              value={selectedTuning}
              onChange={(e) => setSelectedTuning(e.target.value as TuningType)}
              disabled={isPlaying}
              className="bg-[#1a150f] text-[#7a6a50] text-[10px] tracking-wide border-none rounded px-3 py-1.5 focus:outline-none cursor-pointer"
            >
              {Object.entries(TUNINGS).map(([key, data]) => (
                <option key={key} value={key} className="bg-[#1a150f]">
                  {data.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center mt-3 text-[8px] text-[#555] tracking-wider">
        <div className="relative inline-block">
          {isPlaying ? (
            <button onClick={stopSong} className="text-[#b89a5a] hover:text-[#c9aa6a]">
              ■ Stop
            </button>
          ) : (
            <>
              <button
                onClick={() => setShowDemoMenu(!showDemoMenu)}
                className="hover:text-[#7a6a50]"
              >
                ▶ Demo
              </button>
              {showDemoMenu && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-[#2d2822] rounded-lg shadow-lg border border-[#3d3428] py-2 px-1 z-50">
                  {SONGS.map((song) => (
                    <button
                      key={song.id}
                      onClick={() => {
                        playSong(song.id);
                        setShowDemoMenu(false);
                      }}
                      className="block w-full text-left text-[9px] text-[#7a6a50] hover:text-[#b89a5a] hover:bg-[#1a150f] px-3 py-1.5 rounded whitespace-nowrap"
                    >
                      {song.title}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <span className="hidden md:inline"> · L: Q-U · R: H-] · U: 1-6</span>
      </div>
    </div>
  );
}

function MbiraKey({
  keyDef,
  isActive,
  onClick,
  width,
  showLabel,
  manual,
}: {
  keyDef: MbiraKeyDef;
  isActive: boolean;
  onClick: () => void;
  width: number;
  showLabel: boolean;
  manual: "left" | "right" | "upper";
}) {
  const height = manual === "upper" ? 10 : 12;
  const isRoot = keyDef.id.startsWith("G");

  const baseColors = {
    left: isActive
      ? "linear-gradient(180deg, #d0d0d0 0%, #f0f0f0 50%, #d8d8d8 100%)"
      : "linear-gradient(180deg, #888 0%, #b0b0b0 50%, #909090 100%)",
    right: isActive
      ? "linear-gradient(180deg, #d8d8d8 0%, #f8f8f8 50%, #e0e0e0 100%)"
      : "linear-gradient(180deg, #909090 0%, #c0c0c0 50%, #989898 100%)",
    upper: isActive
      ? "linear-gradient(180deg, #c8c8c8 0%, #e8e8e8 50%, #d0d0d0 100%)"
      : "linear-gradient(180deg, #808080 0%, #a8a8a8 50%, #888888 100%)",
  };

  return (
    <div
      onPointerDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      className={`relative transition-transform duration-50 cursor-pointer touch-none rounded-sm ${
        isActive ? "scale-95" : "hover:brightness-110"
      }`}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        background: baseColors[manual],
        boxShadow: isActive
          ? "0 1px 2px rgba(0,0,0,0.3)"
          : "0 2px 4px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.3)",
      }}
    >
      {/* Root note marker */}
      {isRoot && (
        <div
          className={`absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full ${
            manual === "left" ? "right-2" : "left-2"
          } ${isActive ? "bg-[#b89a5a]" : "bg-[#555]"}`}
        />
      )}
      {/* Note label */}
      {showLabel && (
        <div
          className={`absolute top-1/2 -translate-y-1/2 text-[6px] font-bold text-[#444] ${
            manual === "left" ? "left-1" : "right-1"
          }`}
        >
          {keyDef.id.replace(/(\d)/, "")}
        </div>
      )}
    </div>
  );
}

function Knob({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  const rotation = (value - 0.5) * 270;
  const dragRef = useRef<{ startY: number; startValue: number } | null>(null);

  const handleStart = (clientY: number) => {
    dragRef.current = { startY: clientY, startValue: value };
  };

  useEffect(() => {
    const handleMove = (clientY: number) => {
      if (!dragRef.current) return;
      const delta = (dragRef.current.startY - clientY) / 100;
      const newValue = Math.max(0, Math.min(1, dragRef.current.startValue + delta));
      onChange(newValue);
    };

    const handleEnd = () => {
      dragRef.current = null;
    };

    const onMouseMove = (e: MouseEvent) => handleMove(e.clientY);
    const onTouchMove = (e: TouchEvent) => {
      if (dragRef.current) {
        e.preventDefault();
        handleMove(e.touches[0].clientY);
      }
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", handleEnd);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [onChange]);

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        className="w-8 h-8 rounded-full bg-gradient-to-b from-[#3a3025] to-[#201a15] shadow-[0_2px_4px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.08)] cursor-ns-resize relative touch-none"
        onMouseDown={(e) => handleStart(e.clientY)}
        onTouchStart={(e) => {
          e.preventDefault();
          handleStart(e.touches[0].clientY);
        }}
      >
        <div
          className="absolute top-1.5 left-1/2 w-0.5 h-2.5 bg-[#b89a5a] rounded-full"
          style={{ transform: `translateX(-50%) rotate(${rotation}deg)`, transformOrigin: "bottom center" }}
        />
      </div>
      <span className="text-[8px] text-[#5a4a38] tracking-wider">{label}</span>
    </div>
  );
}
